import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';

// Components
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import Dashboard from './components/Dashboard/Dashboard';
import TaskManagement from './components/Tasks/TaskManagement';
import TeamManagement from './components/Team/TeamManagement';
import ClientManagement from './components/Clients/ClientManagement';
import ProjectManagement from './components/Projects/ProjectManagement';

// New
import Reports from './components/Reports/Reports';
import Login from './components/Auth/Login';
import UserProfile from './components/Profile/UserProfile';
import NotificationPanel from './components/Notifications/NotificationPanel';
import TimeTracking from './components/TimeTracking/TimeTracking';
import LeaveManagement from './components/Leaves/LeaveManagement';
import Settings from './components/Settings/Settings';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider, useData } from './context/DataContext';
import { NotificationProvider } from './context/NotificationContext';

const AppContent = () => {
  const { user, loading } = useAuth();
  const { settings } = useData();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notificationOpen, setNotificationOpen] = useState(false);

  // Update Page Title based on Branding
  useEffect(() => {
    if (settings?.branding?.appName) {
      document.title = settings.branding.appName;
    }
  }, [settings]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar isOpen={sidebarOpen} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        <Header 
          toggleSidebar={() => setSidebarOpen(!sidebarOpen)} 
          toggleNotifications={() => setNotificationOpen(!notificationOpen)}
        />
        <main className="flex-1 p-6 overflow-auto">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/projects" element={<ProjectManagement />} />
            <Route path="/tasks" element={<TaskManagement />} />
            <Route path="/team" element={<TeamManagement />} />
            <Route path="/clients" element={<ClientManagement />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/time-tracking" element={<TimeTracking />} />
            <Route path="/leaves" element={<LeaveManagement />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      <AnimatePresence>
        {notificationOpen && (
          <NotificationPanel onClose={() => setNotificationOpen(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <NotificationProvider>
          <Router>
            <AppContent />
          </Router>
        </NotificationProvider>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;